// src/pages/settings/system/QueueServicePanel.tsx
import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Input, Modal, Select, Space, Tag, Typography } from "antd";
import { DeleteOutlined, ReloadOutlined, WarningOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { formatInZone } from "../../../utils/time";

import { DataTable, SectionCard, StatusTag } from "../../../components/shared";
import { useNotifications } from "../../../context/NotificationContext";
import { extractValidationErrors } from "../../../utils/apiError";
import { httpStatusOf, queueApi } from "../../../api/queues/queueApi";
import type { QueueService } from "../../../api/queues/services";
import {
	ESCALATE_MAX_IDS,
	ESCALATE_NOTE_MAX,
	FAILED_PAGE_SIZE,
	RETRY_ALL_MAX,
	type FailedJobSummary,
	type QueueOverview,
} from "../../../types/queues/QueueTypes";
import FailedJobDrawer from "./FailedJobDrawer";

const { Text } = Typography;

export const OVERVIEW_POLL_MS = 15_000;

const shortClass = (fqcn: string): string => fqcn.split("\\").pop() ?? fqcn;

// StatusTag knows paused/inactive but not the rest, so label + colour are set explicitly.
const STATUS_TAG: Record<string, { label: string; color: string }> = {
	running: { label: "Running", color: "var(--mg-success)" },
	paused: { label: "Paused", color: "var(--mg-warning)" },
	inactive: { label: "Inactive", color: "var(--mg-danger)" },
	unreachable: { label: "Unreachable", color: "var(--mg-danger)" },
	unconfigured: { label: "Not configured", color: "var(--mg-text-secondary)" },
};

// A 403 here means the session reached the service but lacks its ops permission (platform.ops.*) — say so, not "error".
const overviewErrorText = (error: unknown): string => {
	const status = httpStatusOf(error);
	if (status === 403) return "Your account lacks this service's operations permission (403).";
	if (status === 401) return "Not signed in to this service (401).";
	if (status !== undefined) return `Request failed with HTTP ${status}.`;
	return "Service unreachable.";
};

const Tile: React.FC<{ label: string; value: React.ReactNode; tone?: "danger" }> = ({ label, value, tone }) => (
	<div
		style={{
			flex: "1 1 120px",
			padding: "10px 12px",
			border: "1px solid var(--mg-border)",
			borderRadius: 6,
			background: "var(--mg-bg-subtle)",
		}}
	>
		<div style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>{label}</div>
		<div style={{ fontSize: 20, fontWeight: 600, color: tone === "danger" ? "var(--mg-danger)" : "var(--mg-text)" }}>{value}</div>
	</div>
);

interface QueueServicePanelProps {
	service: QueueService;
}

const QueueServicePanel: React.FC<QueueServicePanelProps> = ({ service }) => {
	const queryClient = useQueryClient();
	const { showSuccess, showError, showWarning } = useNotifications();

	const api = useMemo(() => (service.baseUrl ? queueApi(service.baseUrl) : null), [service.baseUrl]);

	const [showFailed, setShowFailed] = useState(false);
	const [queueFilter, setQueueFilter] = useState<string | undefined>(undefined);
	const [page, setPage] = useState(1);
	const [selected, setSelected] = useState<FailedJobSummary[]>([]);
	const [detailId, setDetailId] = useState<string | null>(null);
	const [escalateOpen, setEscalateOpen] = useState(false);
	const [escalateNote, setEscalateNote] = useState("");
	const [retryAllOpen, setRetryAllOpen] = useState(false);

	const overview = useQuery({
		queryKey: ["queue", service.key, "overview"],
		queryFn: () => (api as NonNullable<typeof api>).getOverview(),
		enabled: api !== null,
		refetchInterval: OVERVIEW_POLL_MS,
		refetchIntervalInBackground: false,
		retry: false,
	});

	const failed = useQuery({
		queryKey: ["queue", service.key, "failed", queueFilter ?? "", page],
		queryFn: () => (api as NonNullable<typeof api>).getFailed({ queue: queueFilter, page, per_page: FAILED_PAGE_SIZE }),
		enabled: api !== null && showFailed,
		retry: false,
	});

	const invalidate = () => queryClient.invalidateQueries({ queryKey: ["queue", service.key] });

	const retryOne = useMutation({
		mutationFn: (id: string) => (api as NonNullable<typeof api>).retry(id),
		onSuccess: (result) => {
			showSuccess("Job retried", `Re-queued as ${result.new_id}`);
			setDetailId(null);
			setSelected((rows) => rows.filter((r) => r.id !== result.id));
			void invalidate();
		},
		onError: (error) => showError("Retry failed", extractValidationErrors(error).join(" ")),
	});

	const forgetOne = useMutation({
		mutationFn: (id: string) => (api as NonNullable<typeof api>).forget(id),
		onSuccess: (_result, id) => {
			showSuccess("Failed job deleted");
			setSelected((rows) => rows.filter((r) => r.id !== id));
			void invalidate();
		},
		onError: (error) => showError("Delete failed", extractValidationErrors(error).join(" ")),
	});

	const retryAll = useMutation({
		mutationFn: () => (api as NonNullable<typeof api>).retryAll({ queue: queueFilter, limit: RETRY_ALL_MAX }),
		onSuccess: (result) => {
			setRetryAllOpen(false);
			setSelected([]);
			showSuccess(`Retried ${result.retried} failed job${result.retried === 1 ? "" : "s"}`);
			void invalidate();
		},
		onError: (error) => showError("Retry all failed", extractValidationErrors(error).join(" ")),
	});

	const escalate = useMutation({
		mutationFn: () =>
			(api as NonNullable<typeof api>).escalate({
				failed_job_ids: selected.map((r) => r.id),
				note: escalateNote.trim() || undefined,
			}),
		onSuccess: (result) => {
			setEscalateOpen(false);
			setEscalateNote("");
			setSelected([]);
			showSuccess("Escalated to agx-admin", `Incident #${result.incident_id}`);
		},
		onError: (error) => {
			const status = httpStatusOf(error);
			// 501 = the service has no reporter yet (S14 pending); 503 = reporter present but ADMIN_SYSTEM_URL unset.
			if (status === 501 || status === 503) {
				setEscalateOpen(false);
				showWarning("Escalation is not configured for this service", extractValidationErrors(error).join(" "));
				return;
			}
			showError("Escalation failed", extractValidationErrors(error).join(" "));
		},
	});

	const confirmDelete = (row: FailedJobSummary) =>
		Modal.confirm({
			title: "Delete failed job",
			content: `${shortClass(row.name)} on ${row.queue} will be removed from the failed list. This cannot be undone.`,
			okText: "Delete",
			okType: "danger",
			onOk: () => forgetOne.mutateAsync(row.id),
		});

	const data: QueueOverview | undefined = overview.data;
	const status: string = !api ? "unconfigured" : overview.isError ? "unreachable" : data?.horizon.status ?? "unconfigured";

	const failedColumns: ColumnsType<FailedJobSummary> = [
		{ title: "Job", dataIndex: "name", key: "name", render: (name: string) => <Text strong>{shortClass(name)}</Text> },
		{ title: "Queue", dataIndex: "queue", key: "queue", width: 130 },
		{
			title: "Failed",
			dataIndex: "failed_at",
			key: "failed_at",
			width: 140,
			render: (d: string) => (d ? formatInZone(d, "DD MMM HH:mm") : "—"),
		},
		{ title: "Attempts", dataIndex: "attempts", key: "attempts", width: 90, align: "center" },
		{ title: "Exception", dataIndex: "exception_class", key: "exception_class", width: 180, render: (c: string) => <Text code>{shortClass(c)}</Text> },
		{ title: "Summary", dataIndex: "exception_summary", key: "exception_summary", ellipsis: true },
		{
			title: "",
			key: "actions",
			width: 72,
			render: (_: unknown, row: FailedJobSummary) => (
				<Space size={2}>
					<Button
						type="text"
						size="small"
						icon={<ReloadOutlined />}
						aria-label={`Retry ${row.id}`}
						loading={retryOne.isPending && retryOne.variables === row.id}
						onClick={(e) => {
							e.stopPropagation();
							retryOne.mutate(row.id);
						}}
					/>
					<Button
						type="text"
						size="small"
						icon={<DeleteOutlined />}
						danger
						aria-label={`Delete ${row.id}`}
						onClick={(e) => {
							e.stopPropagation();
							confirmDelete(row);
						}}
					/>
				</Space>
			),
		},
	];

	const queueColumns: ColumnsType<QueueOverview["queues"][number]> = [
		{ title: "Queue", dataIndex: "name", key: "name" },
		{ title: "Connection", dataIndex: "connection", key: "connection", width: 140 },
		{ title: "Length", dataIndex: "length", key: "length", width: 90, align: "right" },
		{ title: "Wait (s)", dataIndex: "wait_seconds", key: "wait_seconds", width: 100, align: "right" },
		{ title: "Processes", dataIndex: "processes", key: "processes", width: 100, align: "right" },
	];

	const totalFailed = failed.data?.meta.total ?? 0;
	const failedBadge = data ? data.metrics.recent_failed_total : undefined;

	return (
		<SectionCard
			title={
				<Space size={8}>
					<span>{service.label}</span>
					<StatusTag status={status} color={STATUS_TAG[status]?.color} label={STATUS_TAG[status]?.label ?? status} />
				</Space>
			}
			subtitle={data ? `Updated ${formatInZone(data.generated_at, "HH:mm:ss")}` : undefined}
			extra={
				api && (
					<Button size="small" icon={<ReloadOutlined />} onClick={() => void overview.refetch()} loading={overview.isFetching}>
						Refresh
					</Button>
				)
			}
		>
			{!api && <Alert type="info" showIcon message="This service has no base URL configured for this clinic." />}

			{api && overview.isError && (
				<Alert type="error" showIcon message={overviewErrorText(overview.error)} data-testid={`queue-${service.key}-error`} />
			)}

			{data && (
				<Space direction="vertical" size={12} style={{ width: "100%" }}>
					<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
						<Tile label="Jobs / minute" value={data.metrics.jobs_per_minute} />
						<Tile label="Processed (1h)" value={data.metrics.processed_last_hour} />
						<Tile label="Failed (1h)" value={data.metrics.failed_last_hour} tone={data.metrics.failed_last_hour > 0 ? "danger" : undefined} />
						<Tile label="Failed (recent)" value={data.metrics.recent_failed_total} tone={data.metrics.recent_failed_total > 0 ? "danger" : undefined} />
					</div>

					<DataTable<QueueOverview["queues"][number]>
						bare
						columns={queueColumns}
						dataSource={data.queues}
						rowKey="name"
						pagination={false}
						emptyTitle="No queues reported"
						emptyDescription="Horizon has not published a workload snapshot yet"
					/>

					<Space wrap size={4}>
						{data.horizon.masters.flatMap((m) =>
							m.supervisors.map((s) => (
								<Tag key={s.name} color={s.status === "running" ? "green" : s.status === "paused" ? "gold" : "red"}>
									{s.name.split(":").pop()} · {s.processes} proc · {s.queues.join(", ")}
								</Tag>
							)),
						)}
						{data.horizon.masters.length === 0 && <Text type="secondary">No Horizon master is running.</Text>}
					</Space>

					<Button type={showFailed ? "default" : "primary"} ghost={!showFailed} onClick={() => setShowFailed((v) => !v)}>
						{showFailed ? "Hide failed jobs" : `Show failed jobs${failedBadge !== undefined ? ` (${failedBadge})` : ""}`}
					</Button>

					{showFailed && (
						<Space direction="vertical" size={8} style={{ width: "100%" }}>
							<Space wrap>
								<Select
									allowClear
									placeholder="All queues"
									style={{ minWidth: 160 }}
									value={queueFilter}
									onChange={(v) => {
										setQueueFilter(v || undefined);
										setPage(1);
									}}
									options={data.queues.map((q) => ({ value: q.name, label: q.name }))}
								/>
								<Button icon={<ReloadOutlined />} disabled={totalFailed === 0} onClick={() => setRetryAllOpen(true)}>
									Retry all{queueFilter ? ` on ${queueFilter}` : ""}
								</Button>
								<Button
									icon={<WarningOutlined />}
									danger
									disabled={selected.length === 0 || selected.length > ESCALATE_MAX_IDS}
									onClick={() => setEscalateOpen(true)}
								>
									Escalate selected ({selected.length})
								</Button>
								{selected.length > ESCALATE_MAX_IDS && <Text type="danger">Select at most {ESCALATE_MAX_IDS} jobs to escalate.</Text>}
							</Space>

							{failed.isError && <Alert type="error" showIcon message={extractValidationErrors(failed.error).join(" ")} />}

							<DataTable<FailedJobSummary>
								bare
								columns={failedColumns}
								dataSource={failed.data?.data ?? []}
								rowKey="id"
								loading={failed.isLoading}
								selectedRows={selected}
								setSelectedRows={setSelected}
								total={totalFailed}
								currentPage={page}
								pageSize={FAILED_PAGE_SIZE}
								onPageChange={(p) => setPage(p)}
								onRowClick={(row) => setDetailId(row.id)}
								emptyTitle="No failed jobs"
								emptyDescription={queueFilter ? `Nothing has failed on ${queueFilter} recently` : "Nothing has failed recently"}
							/>
						</Space>
					)}
				</Space>
			)}

			<Modal
				title={`Retry all failed jobs — ${service.label}`}
				open={retryAllOpen}
				onCancel={() => setRetryAllOpen(false)}
				width={480}
				destroyOnClose
				footer={[
					<Button key="cancel" onClick={() => setRetryAllOpen(false)}>
						Cancel
					</Button>,
					<Button key="ok" type="primary" loading={retryAll.isPending} onClick={() => retryAll.mutate()}>
						Retry {Math.min(totalFailed, RETRY_ALL_MAX)}
					</Button>,
				]}
			>
				<p>
					{totalFailed} failed job{totalFailed === 1 ? "" : "s"}
					{queueFilter ? ` on ${queueFilter}` : ""} will be re-queued.
				</p>
				{totalFailed > RETRY_ALL_MAX && (
					<Alert
						type="warning"
						showIcon
						message={`Only ${RETRY_ALL_MAX} jobs are retried per request — run it again for the rest.`}
					/>
				)}
			</Modal>

			<Modal
				title={`Escalate to agx-admin — ${service.label}`}
				open={escalateOpen}
				onCancel={() => setEscalateOpen(false)}
				width={520}
				destroyOnClose
				footer={[
					<Button key="cancel" onClick={() => setEscalateOpen(false)}>
						Cancel
					</Button>,
					<Button key="ok" type="primary" danger loading={escalate.isPending} onClick={() => escalate.mutate()}>
						Escalate {selected.length}
					</Button>,
				]}
			>
				<Space direction="vertical" size={8} style={{ width: "100%" }}>
					<Text>
						{selected.length} selected failure{selected.length === 1 ? "" : "s"} will be reported to the platform team as an incident.
					</Text>
					<Input.TextArea
						aria-label="Escalation note"
						rows={3}
						maxLength={ESCALATE_NOTE_MAX}
						showCount
						placeholder="Optional note for the platform team"
						value={escalateNote}
						onChange={(e) => setEscalateNote(e.target.value)}
					/>
				</Space>
			</Modal>

			{api && (
				<FailedJobDrawer
					serviceKey={service.key}
					serviceLabel={service.label}
					api={api}
					jobId={detailId}
					onClose={() => setDetailId(null)}
					onRetry={(id) => retryOne.mutate(id)}
					retrying={retryOne.isPending}
				/>
			)}
		</SectionCard>
	);
};

export default QueueServicePanel;
